import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AvailableSlot } from '../../types/forms';
import {
  monthMatrix, monthIndex, ymOf, todayISO, formatDayLabel,
  MONTH_NAMES, WEEKDAY_SHORT,
} from '../../lib/calendar';

const hhmm = (t: string) => t.slice(0, 5);

interface Props {
  slots: AvailableSlot[];
  selected: AvailableSlot | null;
  onSelect: (slot: AvailableSlot) => void;
}

// Month-overview slot picker (Calendly-style): a calendar marks the days that
// still have a free place; tapping a day reveals that day's times next to it.
// Replaces the long stacked day-by-day list (which felt overwhelming).
export default function BookingCalendar({ slots, selected, onSelect }: Props) {
  // Slots bucketed per day, each day sorted by start time.
  const byDay = useMemo(() => {
    const map = new Map<string, AvailableSlot[]>();
    for (const s of slots) {
      if (!map.has(s.booking_date)) map.set(s.booking_date, []);
      map.get(s.booking_date)!.push(s);
    }
    for (const list of map.values()) list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [slots]);

  // A day is bookable when at least one of its slots still has a free place.
  const bookableDays = useMemo(() => {
    const set = new Set<string>();
    for (const [iso, list] of byDay) if (list.some((s) => s.free > 0)) set.add(iso);
    return set;
  }, [byDay]);

  const sortedDays = useMemo(() => Array.from(bookableDays).sort(), [bookableDays]);
  const firstDay = sortedDays[0];
  const lastDay = sortedDays[sortedDays.length - 1];

  // The day whose times are shown in the panel. Defaults to an already chosen
  // slot's day (e.g. after switching views) or the earliest bookable day.
  const [activeDay, setActiveDay] = useState<string | null>(
    selected?.booking_date ?? firstDay ?? null,
  );

  // Displayed month, initialised to the active/earliest day's month.
  const [view, setView] = useState(() => ymOf(activeDay ?? firstDay ?? todayISO()));

  // Clamp navigation to the months that actually contain bookable days.
  const minIdx = firstDay ? monthIndex(ymOf(firstDay).year, ymOf(firstDay).month) : monthIndex(view.year, view.month);
  const maxIdx = lastDay ? monthIndex(ymOf(lastDay).year, ymOf(lastDay).month) : monthIndex(view.year, view.month);
  const curIdx = monthIndex(view.year, view.month);

  const cells = useMemo(() => monthMatrix(view.year, view.month), [view]);
  const today = todayISO();

  const step = (delta: number) => {
    const next = view.month + delta;
    setView({ year: view.year + Math.floor(next / 12), month: ((next % 12) + 12) % 12 });
  };

  const pickDay = (iso: string) => {
    setActiveDay(iso);
    // One free time on that day → select it straight away (one-tap booking).
    const free = (byDay.get(iso) ?? []).filter((s) => s.free > 0);
    if (free.length === 1) onSelect(free[0]);
  };

  const daySlots = activeDay ? byDay.get(activeDay) ?? [] : [];

  return (
    <div className="rf-cal">
      <div>
        <div className="rf-cal__nav">
          <button
            type="button" className="rf-cal__navbtn" aria-label="Vorheriger Monat"
            onClick={() => step(-1)} disabled={curIdx <= minIdx}
          >
            <ChevronLeft size={18} />
          </button>
          <span className="rf-cal__month">{MONTH_NAMES[view.month]} {view.year}</span>
          <button
            type="button" className="rf-cal__navbtn" aria-label="Nächster Monat"
            onClick={() => step(1)} disabled={curIdx >= maxIdx}
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="rf-cal__week">
          {WEEKDAY_SHORT.map((w) => <span key={w} className="rf-cal__wd">{w}</span>)}
        </div>

        <div className="rf-cal__days">
          {cells.map((c) => {
            const bookable = c.inMonth && bookableDays.has(c.iso);
            const cls = [
              'rf-cal__day',
              c.inMonth ? '' : 'rf-cal__day--out',
              bookable ? 'rf-cal__day--free' : '',
              c.iso === activeDay ? 'rf-cal__day--active' : '',
              c.iso === today ? 'rf-cal__day--today' : '',
            ].filter(Boolean).join(' ');
            return (
              <button
                key={c.iso} type="button" className={cls}
                disabled={!bookable} aria-pressed={c.iso === activeDay}
                onClick={() => pickDay(c.iso)}
              >
                {c.day}
                {bookable && <span className="rf-cal__dot" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rf-cal__panel">
        {!activeDay && <p className="rf-muted">Wähle einen <strong>markierten Tag</strong>, um die freien Uhrzeiten zu sehen.</p>}
        {activeDay && daySlots.length === 0 && (
          <p className="rf-muted">An diesem Tag sind aktuell keine Termine frei.</p>
        )}
        {activeDay && daySlots.length > 0 && (
          <>
            <div className="rf-cal__paneltitle">{formatDayLabel(activeDay)}</div>
            <div className="rf-slots">
              {daySlots.map((s) => {
                const isSel = selected?.schedule_id === s.schedule_id && selected?.booking_date === s.booking_date;
                return (
                  <button
                    key={s.schedule_id + s.booking_date} type="button"
                    className={`rf-slot ${isSel ? 'rf-slot--selected' : ''}`}
                    disabled={s.free <= 0} onClick={() => onSelect(s)}
                  >
                    <span className="rf-slot__time">{hhmm(s.start_time)}–{hhmm(s.end_time)}</span>
                    <span className="rf-slot__free">
                      {s.free > 0 ? `${s.free} von ${s.trial_capacity} frei` : 'ausgebucht'}
                      {s.room_name ? ` · ${s.room_name}` : ''}
                      {s.requires_confirmation ? ' · Termin nach den Ferien' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
